import { useContext, useEffect, useState } from "react";
import IVendorModel from "../../../models/IVendorModel";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import UIService from "../../../services/UIService";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import { NavLink, useNavigate, useLocation, useMatch } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import SystemVersion from "../../shared/SystemVersion/SystemVersion";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwThemeModeSwitcher } from "../../shared/OwThemeModeSwitcher/OwThemeModeSwitcher";
import styled from "styled-components";
import UserTypesEnum from "../../../models/UserTypesEnum";
import { EnvironmentLabel } from "../../shared/EnvironmentLabel/EnvironmentLabel";
import { HeaderAccountSwitcher } from "../HeaderAccountSwitcher/HeaderAccountSwitcher";
import { Dropdown } from "react-bootstrap";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUpdateTokenSettingsDTO from "../../../models/DTO/IUpdateTokenSettingsDTO";
import UserService from "../../../services/UserService";
import ILoadUserDTO from "../../../models/DTO/ILoadUserDTO";
import "./HeaderFnStandard.scss";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";

type Props = {
    menuIconMode: boolean;
    name?: string;
    toggleMenuIconMode: () => void;
    setMenuNavRef: (e: any) => void;
    toggleMenu: (e: any) => void;
    menuOpen: boolean;
    rightSideOpen: boolean;
    toggleHelpDock: () => void;
    toggleTutorial: () => void;
    doLogout: (e: any) => void;
    mainViewClass: string;
    menuClass: string;
    isOnline: boolean;
    vendorModel: IVendorModel;
    supplierModel: ISupplierModel;
    userModel: IUserUI;
    setActivityIndicator: (activityIndicator: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    hasTutorial: () => boolean;
    hideMenuIconMode: boolean;
    rightHeaderComponent: any;
};

export const HeaderFnStandard = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const entryMatch = useMatch("/:entityType/:order_guid/entry");
    const ecomMatch = useMatch("/:entityType/:order_guid/ecom/*");

    const location = useLocation();
    const history = useNavigate();

    // const [menuOpen, setMenuOpen] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false);

    // Get current theme state
    const getTheme = () => {
        const { themeType } = UIService.getThemeType(supplierModel, userModel);
        return themeType.toUpperCase() === "LIGHT";
    };

    const [isLightMode, setIsLightMode] = useState(getTheme());

    useEffect(() => {
        if (entryMatch) {
            uiContext.setIsEcommView(false);
        }

        if (ecomMatch) {
            uiContext.setIsEcommView(true);
        }
    }, [entryMatch, ecomMatch]);

    const toggleTutorial = () => {
        if (!props.hasTutorial()) {
            return null;
        }

        props.toggleTutorial();
    };

    const toggleEcommerceView = () => {
        if (uiContext.isEcommView) {
            history({
                pathname: location.pathname.replace("/checkout", "").replace("ecom", "entry"),
            });

            uiContext.setIsEcommView(false);
        } else {
            // take the user from /orders/:guid/entry to /orders/:guid/ecom
            history({
                pathname: location.pathname.replace("entry", "ecom"),
            });

            uiContext.setIsEcommView(true);
        }
    };

    const isOrderEntryView = (): boolean => {
        let isOrderEntryView = false;

        if (location.pathname.includes("orders") || location.pathname.includes("templates") || location.pathname.includes("quotes")) {
            if (location.pathname.includes("entry")) {
                isOrderEntryView = true;
            }

            if (location.pathname.includes("ecom")) {
                isOrderEntryView = true;
            }
        }

        // were on the ecomm view (Is Ecommerce View) user / account
        if (location.pathname.includes("ecomm")) {
            isOrderEntryView = false;
        }

        return isOrderEntryView;
    };

    const toggleOnlineMode = () => {
        // UIService.offlineModeSubject$().next(!isOnline);
    };

    const toggleAccountSwitcherOpen = (isOpen: boolean) => {
        setAccountSwitcherOpen(isOpen);
    };

    // Theme toggle function for dropdown
    const toggleTheme = () => {
        const newThemeMode = !isLightMode;
        setIsLightMode(newThemeMode);

        const currentToken = UserService.getCurrentToken(userModel);

        if (currentToken) {
            const payload: IUpdateTokenSettingsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                settings: {
                    theme: newThemeMode ? "LIGHT" : "DARK",
                },
                tokenGuid: currentToken.guid,
            };

            socket.emit(EndpointsEnum.TokenUpdateSettings, payload);
            uiContext.setActivityIndicator(true);

            // Reload user to apply theme changes
            const loadPayload: ILoadUserDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
            };
            socket.emit(EndpointsEnum.AppLoadUser, loadPayload);
        }
    };

    let onlineStatusIcon = "fad fa-wifi-1";

    if (!isOnline || !navigator.onLine) {
        onlineStatusIcon = "fad fa-wifi-slash";
    }

    let hideHelp = true;

    if (supplierModel && userModel) {
        if (!userModel.isSupplierUser) {
            if (supplierModel.hide_vendor_feedback_help) {
                hideHelp = true;
            } else {
                hideHelp = false;
            }
        } else {
            hideHelp = false;
        }
    }

    const hasTutorial = props.hasTutorial();

    let hasASideOpen = false;

    if (uiContext.menuOpen || props.rightSideOpen) {
        hasASideOpen = true;
    }

    let leftSideClass = "col-0 d-none";
    let rightSideClass = "col-0 d-none";

    if (uiContext.menuOpen) {
        leftSideClass = `align-items-center d-flex ${props.menuClass}`;
    }

    if (props.rightSideOpen) {
        if (uiContext.menuIconMode && uiContext.menuOpen) {
            rightSideClass = `col-sm-4 col-md-3 col-lg-3 col-xl-2 sideBarNavWrapper`;
        } else {
            rightSideClass = `align-items-center d-flex ${props.menuClass}`;
        }
    }

    const logActiveListeners = () => {
        // @ts-expect-error
        console.log("Active Listeners: ", socket._callbacks);
    };

    return (
        <HeaderContainer id={"HeaderStandard"} className="HeaderStandard HeaderFnStandard d-none d-md-flex d-print-none">
            {/* Left Header */}
            <div className={leftSideClass}>
                <div className={"d-flex justify-content-between w-100 px-3"}>
                    <div className={"d-flex align-items-center"}>
                        <i className="fas fa-user me-2" title={props.name} /> {uiContext.menuIconMode ? null : props.name}
                    </div>
                    {props.hideMenuIconMode ? null : (
                        <span className={"ms-auto d-flex align-items-center pointer"} onClick={props.toggleMenuIconMode}>
                            <i className={"fas " + (uiContext.menuIconMode ? "fa-chevron-right" : "fa-chevron-left")} />
                        </span>
                    )}
                </div>
            </div>

            {/* Center Header */}
            <div
                className={
                    "d-flex justify-content-between align-items-center px-3 " +
                    (hasASideOpen ? props.mainViewClass : "col-12") +
                    " align-items-center"
                }
                style={{
                    boxShadow: "0 2px 4px 0 #00000080",
                    height: "4rem ",
                    flex: "1 1",
                }}>
                <div className="d-flex">
                    {!UIService.isLoginPath() ? (
                        <div
                            id={"menu"}
                            ref={props.setMenuNavRef}
                            tabIndex={1}
                            onClick={props.toggleMenu}
                            className="d-inline-flex flex-column align-items-center header-icon me-3 mt-2">
                            <i className={"fas fa-bars " + (uiContext.menuOpen ? "d-none" : "")} />
                            <i className={"fas fa-times " + (uiContext.menuOpen ? "" : "d-none")} />
                            <div>
                                <FormattedMessage id="app.menu" defaultMessage="Menu" description="Menu" />
                            </div>
                        </div>
                    ) : null}
                    <div className="d-flex flex-column">
                        <div>
                            <AccountHeaderContainer>
                                <HeaderAccountSwitcher
                                    menuIconMode={props.menuIconMode}
                                    hideMenuIconMode={props.hideMenuIconMode}
                                    toggleMenuIconMode={props.toggleMenuIconMode}
                                    toggleAccountSwitcherOpen={toggleAccountSwitcherOpen}
                                    socket={socket}
                                />
                            </AccountHeaderContainer>
                        </div>
                        <div className={"d-flex"}>
                            <EnvironmentLabel />
                            <SystemVersion />
                        </div>
                    </div>
                </div>

                {props.supplierModel.has_ecommerce_view && isOrderEntryView() ? (
                    <div className={"view-switcher py-2 px-4 rounded-5"}>
                        <OwLabel
                            noBottomMargin={true}
                            title={uiContext.isEcommView ? "line_item_view" : "photo_view"}
                            leftIconClass={uiContext.isEcommView ? "fad fa-table-rows icon" : "fad fa-image icon"}
                            className={"pointer"}
                            fn={toggleEcommerceView}
                            skipFnClassNames={true}
                        />
                    </div>
                ) : null}

                {uiContext.showHiddenFeatures ? (
                    <div>
                        <div>
                            <OwLabel title={"log_active_listeners"} fn={logActiveListeners} />
                        </div>
                    </div>
                ) : null}

                <div className="d-flex align-items-center">
                    {/* THIS CAUSES THE HEADER TO DISAPPEAR ON SAFARI IF IN OFFLINE MODE AND DOING A TEXT INPUT ~75% of the time. Possibly something with an overflow issue. */}
                    {/*{!props.isOnline || !navigator.onLine ? (*/}
                    {/*    <div className="d-flex flex-column white-four header-icon me-5">*/}
                    {/*        <div className={"text-center text-warning"}>*/}
                    {/*            <i className={onlineStatusIcon} />*/}
                    {/*            <div>*/}
                    {/*                <FormattedMessage id="offline" defaultMessage="Offline" description="Offline" />*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*) : null}*/}

                    {!UIService.isLoginPath() ? (
                        <div className={"d-flex align-items-center"}>
                            {/* Deprecated */}
                            {/*{props.supplierModel.show_offline_in_header ? (*/}
                            {/*    <div className={"d-flex"}>*/}
                            {/*        <OwOfflineToggle isOnline={isOnline} toggleFn={toggleOnlineMode} disabled={!navigator.onLine} />*/}
                            {/*    </div>*/}
                            {/*) : null}*/}

                            {/* Theme switcher - only visible on large screens */}
                            <div className="d-none d-lg-block">
                                <OwThemeModeSwitcher />
                            </div>

                            {/* Desktop view - Show icons normally on large screens */}
                            <div id="header-side-icons" className="d-none d-lg-flex align-items-center">
                                <div className={"header-icon me-5"}>
                                    <a
                                        tabIndex={1}
                                        onClick={toggleTutorial}
                                        className={(hasTutorial ? "" : "text-muted") + " text-center"}
                                        id={"tutorial"}>
                                        <i className="fad fa-graduation-cap" />
                                        <div>
                                            <FormattedMessage id="app.tutorial" defaultMessage="Tutorial" description="Tutorial" />
                                        </div>
                                    </a>
                                </div>

                                {hideHelp ? null : (
                                    <div className="d-flex flex-column white-four header-icon">
                                        <a tabIndex={1} onClick={props.toggleHelpDock} className="text-center">
                                            <i className="fad fa-question-circle" />
                                            <div>
                                                <FormattedMessage id="app.help" defaultMessage="Help" description="Help" />
                                            </div>
                                        </a>
                                    </div>
                                )}

                                <div className="d-flex flex-column white-four header-icon ms-5">
                                    <NavLink tabIndex={1} to="/profile">
                                        <i className="fad fa-user-circle" />
                                        <div>
                                            <FormattedMessage id="app.profile" defaultMessage="Profile" description="Profile" />
                                        </div>
                                    </NavLink>
                                </div>

                                {userModel?.isSupplierUser && userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN ? (
                                    <div className="d-flex flex-column white-four header-icon ms-5">
                                        <NavLink tabIndex={1} to="/billing">
                                            <i className="fad fa-dollar-circle" />
                                            <div>
                                                <FormattedMessage id="billing" defaultMessage="Billing" description="Billing" />
                                            </div>
                                        </NavLink>
                                    </div>
                                ) : null}

                                <a
                                    id={"logout"}
                                    tabIndex={1}
                                    className="d-flex flex-column white-four header-icon ms-5 me-2"
                                    onClick={props.doLogout}>
                                    <i className="fad fa-sign-out" />
                                    <div>
                                        <FormattedMessage id="app.logout" defaultMessage="Logout" description="Logout" />
                                    </div>
                                </a>
                            </div>

                            {/* Medium view - Show dropdown on medium screens */}
                            <div className="d-flex d-lg-none align-items-center">
                                <Dropdown align="end" className="header-options-dropdown">
                                    <Dropdown.Toggle variant="link" className="header-options-toggle">
                                        <i className="fas fa-ellipsis-v" />
                                        <div>
                                            <FormattedMessage id="app.options" defaultMessage="Options" description="Options" />
                                        </div>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={toggleTheme}>
                                            <i className={isLightMode ? "fad fa-moon me-2" : "fad fa-sun me-2"} />
                                            {isLightMode ? (
                                                <FormattedMessage id="app.dark_mode" defaultMessage="Dark Mode" description="Dark Mode" />
                                            ) : (
                                                <FormattedMessage
                                                    id="app.light_mode"
                                                    defaultMessage="Light Mode"
                                                    description="Light Mode"
                                                />
                                            )}
                                        </Dropdown.Item>

                                        <Dropdown.Divider />

                                        <Dropdown.Item
                                            onClick={toggleTutorial}
                                            className={hasTutorial ? "" : "text-muted"}
                                            disabled={!hasTutorial}>
                                            <i className="fad fa-graduation-cap me-2" />
                                            <FormattedMessage id="app.tutorial" defaultMessage="Tutorial" description="Tutorial" />
                                        </Dropdown.Item>

                                        {hideHelp ? null : (
                                            <Dropdown.Item onClick={props.toggleHelpDock}>
                                                <i className="fad fa-question-circle me-2" />
                                                <FormattedMessage id="app.help" defaultMessage="Help" description="Help" />
                                            </Dropdown.Item>
                                        )}

                                        <Dropdown.Item as={NavLink} to="/profile">
                                            <i className="fad fa-user-circle me-2" />
                                            <FormattedMessage id="app.profile" defaultMessage="Profile" description="Profile" />
                                        </Dropdown.Item>

                                        {userModel?.isSupplierUser && userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN ? (
                                            <Dropdown.Item as={NavLink} to="/billing">
                                                <i className="fad fa-dollar-circle me-2" />
                                                <FormattedMessage id="billing" defaultMessage="Billing" description="Billing" />
                                            </Dropdown.Item>
                                        ) : null}

                                        <Dropdown.Divider />

                                        <Dropdown.Item onClick={props.doLogout}>
                                            <i className="fad fa-sign-out me-2" />
                                            <FormattedMessage id="app.logout" defaultMessage="Logout" description="Logout" />
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Right Header */}
            <div className={rightSideClass}>{props.rightHeaderComponent}</div>
        </HeaderContainer>
    );
};

const HeaderContainer = styled.div`
    background-color: var(--main-bg-color);
    height: 4rem;
    z-index: 2;

    > div:first-of-type {
        background-color: var(--highlight-color);
        color: var(--highlight-color-text);
    }

    a,
    a:hover {
        color: var(--main-text-color) !important;

        &.text-muted {
            color: var(--minor-bg-color) !important;
            cursor: default;
        }
    }

    a:hover {
        text-decoration: underline;
    }

    .switcher {
        i {
            color: var(--highlight-color);
        }
    }
`;

const AccountHeaderContainer = styled.div``;
